if (!customElements.get('ls-purchase-bar')) {
  customElements.define('ls-purchase-bar', class extends HTMLElement {
    connectedCallback() {
      this.root = document.getElementById(`MainProduct-${this.dataset.section}`);
      this.action = this.querySelector('[data-purchase-action]');
      if (!this.root || !this.root.querySelector('product-form')) return;
      this.resolving = false;
      this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== this.dataset.section) return;
        this.resolvedVariant = data.variant;
        this.resolving = false;
        this.schedule();
      });
      this.unsubscribeOptions = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
        if (!this.root.contains(data.event.target)) return;
        this.resolving = true;
        this.schedule();
      });
      this.financingConfigs = new Map();
      this.financingSelector = `#${CSS.escape(this.root.id)} [data-purchase-total-price]`;
      this.financingAmount = null;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.action.addEventListener('click', () => this.activate(), options);
      this.root.addEventListener('change', () => this.schedule(), options);
      this.root.addEventListener('input', () => this.schedule(), options);
      window.addEventListener('scroll', () => this.schedule(), { ...options, passive: true });
      window.addEventListener('resize', () => this.schedule(), options);
      document.addEventListener('focusin', () => this.schedule(), options);
      document.addEventListener('focusout', () => this.schedule(), options);
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.root, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'value', 'data-price', 'data-base-price', 'data-available', 'data-composition']
      });
      this.update();
    }

    disconnectedCallback() {
      this.unsubscribeVariant?.();
      this.unsubscribeOptions?.();
      this.controller?.abort();
      this.observer?.disconnect();
      this.updateQueued = false;
      this.financingConfigs?.forEach((original, config) => {
        if (config.priceSel === this.financingSelector) Object.assign(config, original);
      });
      this.financingConfigs?.clear();
    }

    schedule() {
      if (this.updateQueued) return;
      this.updateQueued = true;
      // A price change must not wait for an animation frame in a background tab.
      queueMicrotask(() => {
        this.updateQueued = false;
        if (this.isConnected) this.update();
      });
    }

    write(node, value) {
      if (node && node.textContent !== value) node.textContent = value;
    }

    get variant() {
      const formId = this.root.querySelector('product-form input[name="id"]')?.value;
      if (this.resolvedVariant && String(this.resolvedVariant.id) === formId) return this.resolvedVariant;
      const sources = Array.from(this.root.querySelectorAll('variant-selects [data-selected-variant]'))
        .filter(source => source.closest('variant-selects').style.display !== 'none');
      if (!sources.length) sources.push(this.querySelector('[data-initial-variant]'));
      for (const source of sources) {
        try {
          const variant = JSON.parse(source?.textContent);
          if (variant && String(variant.id) === formId) return variant;
        } catch {}
      }
      return null;
    }

    syncFinancing(total) {
      const currency = this.dataset.currency || 'EUR';
      const validAmount = Number.isSafeInteger(total) && total >= 0;
      const paypal = this.root.querySelector('[data-paypal-financing]');
      if (paypal) {
        paypal.hidden = !validAmount || currency !== 'EUR' ||
          total < Number(paypal.dataset.minAmount) || total > Number(paypal.dataset.maxAmount);
      }
      if (!validAmount || !this.root.querySelector('[data-purchase-total-price]')) return;
      const facade = window.SequraWidgetFacade;
      if (!Array.isArray(facade?.widgets) || typeof facade.scheduleRefresh !== 'function') return;
      let rebound = false;
      let matched = false;
      facade.widgets.forEach((config) => {
        if (!config.dest) return;
        try { if (!this.root.querySelector(config.dest)) return; } catch { return; }
        matched = true;
        if (config.priceSel === this.financingSelector && !config.altPriceTriggerSel) return;
        if (!this.financingConfigs.has(config)) {
          this.financingConfigs.set(config, {
            priceSel: config.priceSel,
            altPriceSel: config.altPriceSel,
            altPriceTriggerSel: config.altPriceTriggerSel
          });
        }
        config.priceSel = this.financingSelector;
        config.altPriceSel = '';
        config.altPriceTriggerSel = '';
        rebound = true;
      });
      if (matched && (rebound || this.financingAmount !== total)) {
        this.financingAmount = total;
        facade.scheduleRefresh();
      }
    }

    update() {
      const native = this.root.querySelector('product-form [name="add"]');
      if (!native) { this.hidden = true; return; }
      const rect = native.getBoundingClientRect();
      const preview = document.getElementById('PBarNextFrame');
      const previewHeight = preview ? Math.min(64, preview.getBoundingClientRect().height) : 0;
      this.style.setProperty('--ls-preview-bottom', `${previewHeight}px`);
      const availableBottom = window.innerHeight - previewHeight;
      const top = Math.max(0, document.querySelector('.ls-topline__rail')?.getBoundingClientRect().bottom || 0);
      const nativeVisible = rect.top >= top && rect.bottom <= availableBottom;
      const title = this.root.querySelector('.product__title')?.getBoundingClientRect();
      const compactLandscape = window.matchMedia('(orientation: landscape) and (max-height: 500px) and (max-width: 1100px)').matches;
      const focusedInput = document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"]),textarea,select');
      this.hidden = nativeVisible || (title && title.top >= availableBottom) || focusedInput || (compactLandscape && title && title.bottom > top);
      const variant = this.variant;
      this.ready = Boolean(variant?.id) && !this.resolving;
      if (this.resolving) {
        this.write(this.querySelector('[data-purchase-choice]'), this.dataset.resolvingLabel);
        this.write(this.querySelector('[data-purchase-price]'), '…');
        this.write(this.root.querySelector('[data-purchase-total-price]'), '…');
        this.write(this.action, this.dataset.resolvingLabel);
        this.action.disabled = true;
        this.action.setAttribute('aria-busy', 'true');
        return;
      }
      let extras = 0;
      let pieces = 0;
      const quantityInput = this.root.querySelector('[name="quantity"]:not(:disabled)');
      const quantity = Math.max(1, Number(quantityInput?.value) || 1);
      const addons = this.root.querySelector('ls-modular-addons');
      if (addons?.dataset.composition === 'true') {
        addons.querySelectorAll('[data-addon-role]').forEach((row) => {
          const input = row.querySelector('input');
          const count = Math.max(0, Number(input.value) || 0);
          if (!input.disabled && Number.isFinite(count)) {
            extras += count * Number(row.dataset.price);
            pieces += count;
          }
        });
      }
      let services = 0;
      this.root.querySelectorAll('[data-service-checkbox]:checked:not(:disabled)').forEach((input) => {
        extras += Number(input.dataset.price);
        services += 1;
      });
      const total = Number(variant?.price || 0) * quantity + extras;
      const price = new Intl.NumberFormat(document.documentElement.lang || 'es', {
        style: 'currency', currency: this.dataset.currency || 'EUR'
      }).format(total / 100);
      const picker = Array.from(this.root.querySelectorAll('ls-modular-picker')).find(item => item.closest('variant-selects')?.style.display !== 'none');
      const piece = picker?.dataset.mode === 'extra' ? picker.querySelector('[data-ls-piece-name][aria-pressed="true"]') : null;
      const choice = piece ? `${quantity} × ${piece.dataset.lsPieceName} · ${variant?.options?.[0] || ''}` : (variant?.options || []).filter((option) => option !== 'Default Title').join(' · ');
      const pieceInput = picker?.querySelector('[data-ls-piece-quantity]');
      const validQuantity = !piece || (pieceInput?.validity.valid && Number.isSafeInteger(Number(pieceInput.value)));
      if (piece && !native.disabled && native.getAttribute('aria-disabled') !== 'true') {
        const label = quantity === 1 ? piece.dataset.lsAddOne : piece.dataset.lsAddMany.replace('[count]', quantity);
        this.write(native.querySelector('span'), label);
      }
      if (piece) this.write(this.root.querySelector('.ls-selection__value'), choice);
      const extraLabel = pieces || services ? ` · ${this.dataset.extraLabel}` : '';
      this.write(this.querySelector('[data-purchase-choice]'), `${choice}${extraLabel}`);
      this.write(this.querySelector('[data-purchase-price]'), price);
      this.write(this.querySelector('[data-purchase-name]'), this.dataset.productName);
      this.querySelector('[data-purchase-choice]').title = `${choice}${extraLabel}`;
      const inline = this.root.querySelector('[data-purchase-total]');
      if (inline) {
        inline.hidden = !variant || !Number.isFinite(total);
        this.write(inline.querySelector('[data-purchase-total-price]'), price);
      }
      this.syncFinancing(total);
      const busy = native.getAttribute('aria-disabled') === 'true' && native.classList.contains('loading');
      const disabled = native.disabled || native.getAttribute('aria-disabled') === 'true' || !variant?.available || !validQuantity;
      this.action.disabled = this.ready ? disabled : busy || this.resolving;
      this.action.setAttribute('aria-busy', String(busy));
      this.write(this.action, !this.ready ? this.dataset.chooseLabel : busy ? this.dataset.updateLabel : native.querySelector('span')?.textContent.trim() || native.textContent.trim());
      this.action.dataset.mode = this.ready ? 'add' : 'choose';
    }

    activate() {
      const native = this.root.querySelector('product-form [name="add"]');
      const form = native?.form;
      if (!this.ready) {
        const picker = this.root.querySelector('variant-selects') || native;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        picker.scrollIntoView({ behavior: reduceMotion ? 'instant' : 'smooth', block: 'start' });
        picker.querySelector('input:checked:not(:disabled),select:not(:disabled)')?.focus({ preventScroll: true });
        return;
      }
      if (!form || native.disabled || native.getAttribute('aria-disabled') === 'true') return;
      this.action.disabled = true;
      form.requestSubmit(native);
      this.schedule();
    }
  });
}
